import { useState, useEffect, useRef, useCallback } from "react";
import {
  ScanSearch, Upload, Camera, FileText, Image as ImageIcon,
  AlertTriangle, ShieldAlert, Info, Scale, Sparkles, Eye, Copy, Check,
  Loader2, X, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageIntro from "../components/PageIntro";

/**
 * Contract Risk Radar — public-facing UI.
 *
 * Flow:
 *   1. User picks jurisdiction (Dubai / North Cyprus / Germany).
 *   2. User uploads a file (drag/drop, click, or smartphone camera).
 *   3. POST /api/contract-radar/scan → AI returns structured findings.
 *   4. Findings render grouped by severity colour with copy-paste rebuttal
 *      language for every red/amber clause.
 *
 * Hard rules per user spec:
 *   • The uploaded contract is NEVER persisted (no Mongo, no R2) — backend
 *     only logs metadata.
 *   • 5 scans per user per day (server-enforced; we mirror the count here).
 *   • Smartphone camera + gallery + PDF + DOCX all supported.
 */

// Mirror SEVERITIES from /app/backend/services/contract_radar.py so the badge
// colours, ordering, and copy stay in lock-step with the API contract.
const SEVERITY_META = {
  critical: {
    label: "Critical",
    icon: ShieldAlert,
    // Deep red — money-loss / legal-void territory.
    text: "text-red-300",
    bg: "bg-red-500/10",
    border: "border-red-500/40",
    dot: "bg-red-400",
  },
  high: {
    label: "High",
    icon: AlertTriangle,
    text: "text-orange-300",
    bg: "bg-orange-500/10",
    border: "border-orange-500/40",
    dot: "bg-orange-400",
  },
  medium: {
    label: "Medium",
    icon: AlertTriangle,
    text: "text-yellow-300",
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/40",
    dot: "bg-yellow-400",
  },
  legal: {
    label: "Legal / Compliance",
    icon: Scale,
    text: "text-sky-300",
    bg: "bg-sky-500/10",
    border: "border-sky-500/40",
    dot: "bg-sky-400",
  },
  info: {
    label: "Strong clause",
    icon: Sparkles,
    text: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/40",
    dot: "bg-emerald-400",
  },
  clarity: {
    label: "Clarity / Ambiguity",
    icon: Eye,
    text: "text-zinc-200",
    bg: "bg-white/[0.04]",
    border: "border-white/[0.12]",
    dot: "bg-white/60",
  },
};

const JURISDICTIONS = [
  { value: "dubai", label: "Dubai (UAE)", flag: "🇦🇪" },
  { value: "north_cyprus", label: "North Cyprus (TRNC)", flag: "🇨🇾" },
  { value: "germany", label: "Germany", flag: "🇩🇪" },
];

const ACCEPTED_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
];

const MAX_BYTES = 20 * 1024 * 1024;

function bytesHuman(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}


function CopyButton({ text, testId }) {
  // Two-second "copied!" confirmation, then reverts. We deliberately avoid
  // a toast here because the button itself is the affordance the user is
  // looking at — keeps the focus tight.
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please select the text manually.");
    }
  }, [text]);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-white/60 hover:text-[#D4AF37] transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}


function FindingCard({ finding, idx }) {
  const meta = SEVERITY_META[finding.severity] || SEVERITY_META.medium;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`finding-${finding.severity}-${idx}`}
      className={`border ${meta.border} ${meta.bg} p-5 md:p-6`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-9 h-9 ${meta.bg} ${meta.border} border flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${meta.text}`} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1.5 text-[9px] tracking-[0.2em] uppercase ${meta.text} font-medium`}>
              <span className={`w-1.5 h-1.5 ${meta.dot}`} />
              {meta.label}
            </span>
          </div>
          <h3 className="text-white text-base md:text-lg leading-snug font-medium" data-testid={`finding-headline-${idx}`}>
            {finding.headline}
          </h3>
        </div>
      </div>

      {finding.evidence && (
        <div className="mb-3">
          <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 mb-1.5">Clause from contract</p>
          <p className="text-white/85 text-sm leading-relaxed italic border-l-2 border-white/15 pl-3">
            {finding.evidence}
          </p>
        </div>
      )}

      {finding.explanation && (
        <div className="mb-3">
          <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 mb-1.5">Why it matters</p>
          <p className="text-white/85 text-sm leading-relaxed">{finding.explanation}</p>
        </div>
      )}

      {finding.rebuttal && finding.severity !== "info" && (
        <div className="bg-black/30 border border-[#D4AF37]/25 p-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">Suggested rebuttal · paste to developer</p>
            <CopyButton text={finding.rebuttal} testId={`copy-rebuttal-${idx}`} />
          </div>
          <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">
            {finding.rebuttal}
          </p>
        </div>
      )}
    </div>
  );
}


export default function ContractRadarPage() {
  const [jurisdiction, setJurisdiction] = useState("dubai");
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Fetch usage on mount and after each scan so the "X scans left today"
  // counter stays accurate without a manual refresh.
  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/contract-radar/usage");
      setUsage(data);
    } catch {
      // Non-fatal — counter just won't render.
    }
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const onPickFile = (f) => {
    setError(null);
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type)) {
      setError("Unsupported file type. Please upload PDF, DOCX, JPG, PNG, or WEBP.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`File too large (${bytesHuman(f.size)}). Max ${bytesHuman(MAX_BYTES)}.`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onPickFile(f);
  };

  const startScan = async () => {
    if (!file) {
      setError("Please pick a file first.");
      return;
    }
    setError(null);
    setResult(null);
    setScanning(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("jurisdiction", jurisdiction);
      const { data } = await api.post("/contract-radar/scan", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000, // GPT-5.2 vision on a multi-page contract takes time.
      });
      setResult(data.scan);
      toast.success(`${data.scan.findings.length} findings · ${data.scan.counts.critical} critical, ${data.scan.counts.high} high`);
      fetchUsage();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Scan failed. Please try again.";
      setError(detail);
      // 429 means the daily quota is gone — refresh the counter so the UI
      // disables the button and the user sees their remaining count = 0.
      if (err?.response?.status === 429) fetchUsage();
    } finally {
      setScanning(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const remaining = usage?.remaining_today ?? 5;
  const overLimit = remaining <= 0;

  return (
    <div className="space-y-8" data-testid="contract-radar-page">
      <PageIntro
        pageKey="contract-radar"
        title="Contract Risk Radar"
        subtitle="Upload an SPA, rental contract, or photo of a paper agreement — GPT-5.2 will hunt for investor-trap clauses and give you the exact wording to send back to the developer."
        bullets={[
          {
            icon: ScanSearch,
            label: "Jurisdiction-aware scanning",
            text: "Dubai RERA traps, North Cyprus Aliens' Permission gaps, German Notarvertrag essentials — each scanned against the right legal lens.",
          },
          {
            icon: ShieldAlert,
            label: "Severity-graded findings",
            text: "Critical · High · Medium · Legal · Clarity · Strong-clause. Red flags surface first so you know what to fight on.",
          },
          {
            icon: Sparkles,
            label: "Pasteable rebuttal language",
            text: "Every red/orange flag comes with negotiation wording you can copy straight into an email to the developer.",
          },
        ]}
      />

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] tracking-[0.18em] uppercase text-white/60 mb-2">AI Audit · Private Beta</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Contract Risk Radar
          </h1>
          <p className="text-white/75 text-sm mt-2 max-w-xl">
            Drop in a contract — get a lawyer's-eye first read in under a minute.
            Your file is processed in memory and never stored.
          </p>
        </div>
        {usage && (
          <div className="bg-white/[0.03] border border-white/[0.10] px-4 py-3" data-testid="radar-usage">
            <p className="text-[10px] tracking-[0.18em] uppercase text-white/55 mb-1">Daily Quota</p>
            <p className="text-white font-mono text-lg">
              {remaining}
              <span className="text-white/50 text-sm"> / {usage.daily_limit}</span>
            </p>
            <p className="text-[10px] text-white/55 mt-0.5">scans remaining</p>
          </div>
        )}
      </div>

      {/* Upload + jurisdiction picker */}
      {!result && (
        <div className="border border-white/[0.12] bg-[#0A0A0A] p-6 md:p-8 space-y-6">
          {/* Jurisdiction */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-3">
              Step 1 · Which jurisdiction?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {JURISDICTIONS.map((j) => (
                <button
                  key={j.value}
                  type="button"
                  onClick={() => setJurisdiction(j.value)}
                  data-testid={`jurisdiction-${j.value}`}
                  className={`flex items-center gap-3 px-4 py-3 border transition-colors text-left ${
                    jurisdiction === j.value
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-white/[0.12] hover:border-white/25"
                  }`}
                >
                  <span className="text-xl">{j.flag}</span>
                  <span className={`text-sm ${jurisdiction === j.value ? "text-white" : "text-white/85"}`}>
                    {j.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* File picker / drop zone */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-3">
              Step 2 · Upload the contract
            </p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`border-2 border-dashed transition-colors px-6 py-10 text-center ${
                dragOver
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-white/[0.15] hover:border-white/30"
              }`}
              data-testid="radar-dropzone"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  {file.type.startsWith("image/") ? (
                    <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
                  ) : (
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                  )}
                  <div className="text-left">
                    <p className="text-white text-sm truncate max-w-[300px]">{file.name}</p>
                    <p className="text-white/55 text-xs">{bytesHuman(file.size)} · {file.type.split("/")[1] || file.type}</p>
                  </div>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="radar-clear-file"
                    className="text-white/55 hover:text-white p-1.5"
                    aria-label="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-8 h-8 text-white/40 mx-auto" strokeWidth={1.4} />
                  <p className="text-white/80 text-sm">
                    Drop the contract here, or use one of the buttons below
                  </p>
                  <p className="text-white/50 text-[11px]">
                    Accepted: PDF · DOCX · JPG · PNG · WEBP · up to 20 MB
                  </p>
                </div>
              )}
            </div>

            {/* Hidden inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              className="hidden"
              data-testid="radar-file-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              className="hidden"
              data-testid="radar-camera-input"
            />

            {/* Pick / camera buttons (camera button is mobile-only — desktops
                don't have a camera capture mode, so we still show it but the
                browser falls back to a file picker which is fine.) */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="radar-pick-file-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-white/[0.12] px-4 py-2.5 text-white/85 hover:bg-white/[0.04] transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Browse files
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                data-testid="radar-camera-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-white/[0.12] px-4 py-2.5 text-white/85 hover:bg-white/[0.04] transition-colors"
              >
                <Camera className="w-3.5 h-3.5" />
                Use camera
              </button>
            </div>
          </div>

          {error && (
            <div className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300 text-sm" data-testid="radar-error">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-[10px] text-white/45 max-w-md leading-relaxed">
              Your file is analysed in memory and discarded immediately. EQUIA
              does not store the contract or its extracted text.
            </p>
            <button
              type="button"
              onClick={startScan}
              disabled={!file || scanning || overLimit}
              data-testid="radar-scan-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8C757] disabled:bg-white/[0.08] disabled:text-white/40 text-black text-xs font-semibold tracking-[0.18em] uppercase px-6 py-3 transition-colors"
            >
              {scanning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning…
                </>
              ) : (
                <>
                  <ScanSearch className="w-4 h-4" />
                  {overLimit ? "Quota exhausted" : "Scan contract"}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <ScanResult
          result={result}
          onReset={reset}
        />
      )}

      {/* Always-visible orientation footnote (per user request Feb 28, 2026).
          A second, longer disclaimer also sits at the bottom of the result
          panel — the two are intentional: this one nudges expectations before
          the scan, the other reinforces them after. */}
      <p
        className="text-[11px] text-white/55 italic leading-relaxed max-w-3xl"
        data-testid="radar-orientation-footnote"
      >
        <Info className="w-3 h-3 inline mr-1 mb-0.5" />
        For orientation only. Contract Risk Radar gives you an AI-assisted first read —
        for a binding analysis of your contract, please consult a licensed lawyer in the
        relevant jurisdiction.
      </p>
    </div>
  );
}


function ScanResult({ result, onReset }) {
  const totalFindings = result.findings.length;
  const counts = result.counts;
  // Show only severity bands that actually have findings — keeps the summary
  // strip tight rather than padded with zeros.
  const presentSeverities = Object.entries(counts).filter(([, c]) => c > 0);

  return (
    <div className="space-y-6" data-testid="scan-result">
      {/* Summary header */}
      <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-6 md:p-7">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] mb-1.5">
              Scan complete · {result.jurisdiction_label}
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-white tracking-tight">
              {totalFindings} finding{totalFindings === 1 ? "" : "s"}
            </h2>
            <p className="text-white/55 text-[11px] mt-1 uppercase tracking-[0.12em]">
              Contract type: {result.contract_type.replace(/_/g, " ")}
            </p>
          </div>
          <button
            type="button"
            onClick={onReset}
            data-testid="radar-scan-again"
            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border border-white/[0.15] px-4 py-2.5 text-white/85 hover:bg-white/[0.04] transition-colors whitespace-nowrap"
          >
            New scan
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {result.summary && (
          <p className="text-white/85 text-sm leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4 italic">
            {result.summary}
          </p>
        )}

        {/* Severity strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-5">
          {presentSeverities.map(([sev, n]) => {
            const meta = SEVERITY_META[sev];
            if (!meta) return null;
            return (
              <div
                key={sev}
                className={`${meta.border} ${meta.bg} border px-3 py-2.5`}
                data-testid={`severity-count-${sev}`}
              >
                <p className={`text-[9px] tracking-[0.18em] uppercase ${meta.text}`}>
                  {meta.label}
                </p>
                <p className="text-white text-xl font-mono mt-0.5">{n}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Findings */}
      <div className="space-y-4">
        {result.findings.map((f, i) => (
          <FindingCard key={i} finding={f} idx={i} />
        ))}
      </div>

      {/* Disclaimer */}
      <div className="bg-white/[0.02] border border-white/[0.10] p-5 text-[11px] text-white/55 leading-relaxed">
        <Info className="w-3.5 h-3.5 inline mr-1.5 mb-0.5" />
        Contract Risk Radar is an AI-assisted first-read tool, not a substitute
        for qualified legal counsel. EQUIA does not provide legal advice. For
        any material transaction, please have your contract reviewed by a
        licensed lawyer in the relevant jurisdiction.
      </div>
    </div>
  );
}
